import { ConversionPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { useNavTreeNode } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { IntentOnlySidebarPromo } from "../../../../conversion/locked/intent-only-sidebar-promo";
import {
  CONVERSION_ICON,
  CONVERSION_ICON_FILLED,
  ConversionSuiteStatusesContext,
} from "../../../../redo-suite/conversion-cloud";
import { AbTestItems } from "./ab-tests";
import { AnalyticsItems } from "./analytics";
import { CheckoutItems } from "./checkouts";
import { DashboardItems } from "./dashboard";
import { DiscountRulesItems } from "./discount-rules";
import { PopupsItems } from "./popups";
import { PostPurchaseItems } from "./post-purchase";
import { PricingTestItems } from "./pricing-tests";
import { WidgetsItems } from "./widgets";

export function useConversionNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const {
    checkoutOptimizationEnabled,
    checkoutOutboundRatesEnabled,
    hasConversionSecondaryNav,
  } = useRequiredContext(ConversionSuiteStatusesContext);

  const canViewConversion = useHasPermission(
    ConversionPermissions.ACCESS_CONVERSION,
  );

  if (team.platform !== "shopify" || !canViewConversion) {
    return null;
  }

  const pathRoot = routes.conversionBaseUrl;

  // Conversion off → welcome landing. Conversion on → dashboard / onboard via
  // the existing helper. Predictive alone does not open the suite.
  const conversionHref = routes.getConversionHref({
    enabled: checkoutOptimizationEnabled,
    checkoutOutboundRatesEnabled,
  });

  const primaryTab: LinkPrimaryTab = {
    id: "conversion",
    hasNotification: false,
    UnfilledIcon: CONVERSION_ICON,
    FilledIcon: CONVERSION_ICON_FILLED,
    type: "link",
    href: conversionHref,
    pathRoot,
    friendlyName: "Conversion",
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    hasConversionSecondaryNav
      ? {
          id: "conversion",
          name: "Conversion",
          NavContent: SecondaryConversionSection,
        }
      : undefined;

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: hasConversionSecondaryNav,
  };
}

const SecondaryConversionSection = memo(function SecondaryConversionSection() {
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: "Conversion",
  });
  const { isIntentFocusedDashboard } = useRequiredContext(
    ConversionSuiteStatusesContext,
  );

  // Partial tier (locks present): promote Spotlights next to Dashboard.
  // Full unlocked suite keeps historical order. Items self-hide when
  // locked + presentation is "hidden" (under $3k). Promo visibility is
  // owned by IntentOnlySidebarPromo's internal locks/presentation check.
  return (
    <TreeContext.Provider value={nodeId}>
      {isIntentFocusedDashboard ? (
        <>
          <DashboardItems />
          <PopupsItems />
          <AbTestItems />
          <AnalyticsItems />
          <CheckoutItems />
          <DiscountRulesItems />
          <PostPurchaseItems />
          <PricingTestItems />
          <WidgetsItems />
          <IntentOnlySidebarPromo />
        </>
      ) : (
        <>
          <DashboardItems />
          <AbTestItems />
          <AnalyticsItems />
          <CheckoutItems />
          <DiscountRulesItems />
          <PostPurchaseItems />
          <PricingTestItems />
          <PopupsItems />
          <WidgetsItems />
          <IntentOnlySidebarPromo />
        </>
      )}
    </TreeContext.Provider>
  );
});
